import React from "react";
import SectionHeader from "@/components/SectionHeader";
import MetricRow from "@/components/MetricRow";
import ImageFrame from "@/components/ImageFrame";
import { ArrowUpRight } from "lucide-react";

export default function AudienceSegmentationPage() {
  return (
    <>
      <SectionHeader
        eyebrow="Case Study 07"
        pageIndex="07"
        title="Audience Segmentation & Data-Driven Strategy"
        kicker="We walked away from the beginner race and owned the room nobody was serving."
        lede="The personal finance space is crowded. Everyone fights for the beginner audience: simple tips, basic concepts, 'how to invest ₹500 SIP'. But there's a massive gap: the intermediate investor is underserved. We chose to write for the reader who has already read 10 articles on the topic, and needs more in-depth information."
      />

      {/* Metrics */}
      <section className="mt-20">
        <MetricRow
          items={[
            { value: "2L–4L", label: "Monthly views (organic)" },
            { value: "100%", label: "Editorial standards held" },
            { value: "4+ yr", label: "Target investor experience" },
            { value: "SEO ↑", label: "Compound traffic curve" },
          ]}
        />
      </section>

      {/* Strategy */}
      <section className="mx-auto max-w-[1200px] px-6 pt-28 lg:px-12">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4A574]">The Strategy</p>
        <ol className="mt-8 grid gap-px overflow-hidden border border-[#EDE4D3]/12 bg-[#EDE4D3]/10 md:grid-cols-2">
          {[
            ["Strict editorial standards", "Every article backed by data, not opinions."],
            ["Unique analytical angle", "Don't repeat what competitors say. Find a fresh lens."],
            ["Intermediate-first positioning", "Write for the reader who's done 50 articles already."],
            ["Consistent publishing", "Build a library that compounds in SEO and authority."],
          ].map(([h, d], i) => (
            <li key={h} className="bg-[#0B0E14] p-8">
              <p className="font-serif text-xs italic text-[#D4A574]">0{i + 1}</p>
              <p className="mt-2 font-serif text-2xl">{h}.</p>
              <p className="mt-3 text-sm text-[#EDE4D3]/70">{d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Why it works */}
      <section className="mx-auto max-w-[1100px] px-6 pt-28 lg:px-12">
        <p className="font-serif text-2xl italic leading-snug text-[#EDE4D3]/85 md:text-3xl lg:text-4xl">
          Most brands chase traffic. We chased <span className="text-[#D4A574]">relevance</span>. Relevance is earned through years of quality work, and once you have it with the right segment, you get sticky traffic.
        </p>
        <p className="mt-8 max-w-[68ch] text-[15px] leading-relaxed text-[#EDE4D3]/65">
          The intermediate investor segment is small but mighty. They&rsquo;re the opinion leaders who influence others, the ones with assets to invest, the ones who become long-term customers.
        </p>
      </section>

      {/* Resources */}
      <section className="mx-auto max-w-[1440px] px-6 pt-28 lg:px-12">
        <h2 className="mb-10 font-serif text-3xl tracking-tight lg:text-4xl">
          Where the work <span className="italic text-[#D4A574]">lives</span>.
        </h2>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          {[
            {
              title: "The Learn Hub",
              caption: "For in-app content, built the \u201cLearn Hub\u201d section. Later, we targeted Google Discover through this.",
              image: "ET Money Learn.jpg",
              link: "https://www.etmoney.com/learn",
              cta: "Visit ET Money Learn",
            },
            {
              title: "Complete Author Archive",
              caption: "Every article authored is a body of work spanning mutual funds, market analysis, and personal finance.",
              image: "Author Page.jpg",
              link: "https://www.etmoney.com/learn/author/tinesh-bhasin/",
              cta: "Browse the archive",
            },
          ].map((r) => (
            <a
              key={r.title}
              href={r.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`resource-${r.title.replace(/\s+/g, "-")}`}
              className="group block"
            >
              <div className="overflow-hidden border border-[#EDE4D3]/10 card-lift">
                <ImageFrame filename={r.image} ratio="16/10" />
              </div>
              <div className="mt-5 flex items-start justify-between gap-6">
                <div>
                  <h3 className="font-serif text-2xl leading-tight">{r.title}</h3>
                  <p className="mt-3 max-w-[48ch] text-sm leading-relaxed text-[#EDE4D3]/60">
                    {r.caption}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 font-serif text-[15px] italic text-[#EDE4D3] transition-colors group-hover:text-[#D4A574]">
                    {r.cta}
                    <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                  </span>
                </div>
                <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-[#EDE4D3]/40 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#D4A574]" />
              </div>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}
